import React, { useEffect, useState } from "react";
import { SeshVoiceChannelData } from "../../../../common/sesh_types";
import Select from "react-select";
import { DashboardUtils } from "../dashboard_utils";

interface DropdownDescriptor {
	value: string;
	label: string | JSX.Element;
}

interface ChannelDropdownDescriptor extends DropdownDescriptor {
	channel_data: SeshVoiceChannelData;
	is_disabled?: boolean;
}

interface CategoryDropdownDescriptor extends DropdownDescriptor {
	label: string;
	options: ChannelDropdownDescriptor[];
}

export default function VoiceChannelDropdown(props: {
	voice_channels: SeshVoiceChannelData[];
	selected_channel_id: string;
	updateFunc: (channel_id: string) => void;
	is_stage: boolean;
}) {
	const initial_channel_id =
		props.selected_channel_id ??
		(props.voice_channels ? props.voice_channels[0]?.id : undefined);

	if (!props.voice_channels) {
		return DashboardUtils.RenderLoadingSelect();
	}

	useEffect(() => {
		props.updateFunc(initial_channel_id);
	});

	// TODO: sort on server
	const category_dropdown_map: Map<string, CategoryDropdownDescriptor> =
		new Map();
	const channel_dropdown_map: Map<string, ChannelDropdownDescriptor> =
		new Map();
	const channel_dropdown_descriptors:
		| (ChannelDropdownDescriptor | CategoryDropdownDescriptor)[] = [];
	for (const channel of props.voice_channels) {
		const channel_dropdown = createChannelDropdownDescriptor(channel);
		channel_dropdown_map.set(channel.id, channel_dropdown);

		if (channel.category_id) {
			// this channel is in a category
			const existing_category_dropdown = category_dropdown_map.get(
				channel.category_id
			);
			if (existing_category_dropdown) {
				// category already made
				existing_category_dropdown.options.push(channel_dropdown);
				continue;
			}

			// category has not been made yet
			const category_dropdown_descriptor =
				createCategoryDropdownDesciptor(channel);

			category_dropdown_descriptor.options.push(channel_dropdown);

			channel_dropdown_descriptors.push(category_dropdown_descriptor);
			category_dropdown_map.set(
				category_dropdown_descriptor.value,
				category_dropdown_descriptor
			);
			continue;
		}

		// not in a category
		channel_dropdown_descriptors.push(channel_dropdown);
	}

	let initial_value = channel_dropdown_map.get(initial_channel_id);

	if (!initial_value) {
		initial_value = [...channel_dropdown_map.values()][0];

		if (!initial_value) {
			return (
				<div className="field">
					<label className="label">
						No valid {props.is_stage ? "stage" : "voice"} channels.
					</label>
				</div>
			);
		}
	}

	const is_valid_voice_channel = DashboardUtils.IsValidSeshVoiceChannel(
		initial_value.channel_data,
		props.is_stage
	);

	const label = props.is_stage
		? "Choose a stage channel"
		: "Choose a voice channel";

	return (
		<div className="field">
			<label className="label">{label}</label>
			<div className="control">
				<Select
					//menuIsOpen={true}
					classNamePrefix="sesh-dropdown"
					filterOption={filterOptions}
					//styles={style}
					value={initial_value}
					options={channel_dropdown_descriptors}
					onChange={(channel_dropdown: ChannelDropdownDescriptor) =>
						props.updateFunc(channel_dropdown.channel_data.id)
					}
					isDisabled={false}
				/>
			</div>
			{!is_valid_voice_channel && (
				<InvalidVoiceChannelDescription
					voice_channel={initial_value.channel_data}
				/>
			)}
		</div>
	);
}

function InvalidVoiceChannelDescription(props: {
	voice_channel: SeshVoiceChannelData;
}) {
	const sesh_missing_permissions_element = (
		<div>
			sesh is missing{" "}
			<span className="has-text-weight-bold">
				{props.voice_channel.sesh_missing_perms.join(", ")}
			</span>{" "}
			permissions
		</div>
	);

	const user_missing_permissions_element = (
		<div>
			You are missing{" "}
			<span className="has-text-weight-bold">
				{props.voice_channel.user_missing_perms.join(", ")}
			</span>{" "}
			permissions
		</div>
	);

	return (
		<div className="error-note mt-4">
			{props.voice_channel.user_missing_perms.length > 0 &&
				user_missing_permissions_element}
			{props.voice_channel.sesh_missing_perms.length > 0 &&
				sesh_missing_permissions_element}
		</div>
	);
}

function createChannelDropdownDescriptor(
	channel: SeshVoiceChannelData
): ChannelDropdownDescriptor {
	let icon: JSX.Element;
	let disabled_text_element: JSX.Element;

	const is_valid_channel = DashboardUtils.IsValidSeshVoiceChannel(
		channel,
		channel.is_stage
	);

	const classNames: string[] = [
		"columns is-mobile is-vcentered is-variable is-1",
	];

	if (is_valid_channel) {
		icon = channel.is_stage ? (
			<i className="fa fa-podcast"></i>
		) : (
			<i className="fas fa-volume-up" />
		);
	} else {
		icon = <i className="fas fa-ban" />;
		disabled_text_element = (
			<div className={`column is-pulled-right dropdown-disabled-text`}>
				Invalid Permissions
			</div>
		);

		classNames.push("invalid-permissions-dropdown-element");
	}

	return {
		channel_data: channel,
		label: (
			<div className={classNames.join(" ")}>
				<div className="column is-narrow">
					<span className="icon">{icon}</span>
				</div>
				<div className="column is-narrow">{channel.name}</div>
				{disabled_text_element}
			</div>
		),
		value: channel.id,
		is_disabled: false,
	};
}

function createCategoryDropdownDesciptor(
	channel: SeshVoiceChannelData
): CategoryDropdownDescriptor {
	return {
		value: channel.category_id,
		label: channel.category_name,
		options: [],
	};
}

function filterOptions(
	option: {
		label: string;
		value: string;
		data: { channel_data: SeshVoiceChannelData };
	},
	input: string
) {
	let lowercase_input = input.toLowerCase();
	let lowercase_channel = option.data.channel_data.name.toLowerCase();
	return lowercase_channel.includes(lowercase_input);
}
